import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { api } from '../api/client.js';

/*
 * FIELD GROUPS — keys align with the active v1-26 ruleset dictionaries.
 * Grouping mirrors the prototype's left-column section cards.
 */
const GROUPS = [
  {
    title: 'Applicant',
    fields: [
      { key: 'q_student_dep', label: 'Student is Dependent', type: 'select', options: ['yes', 'no'] },
      { key: 'VarParentDataRequired', label: 'Parent Data Required', type: 'select', options: ['yes', 'no'] },
      { key: 'q_par_marital_status', label: 'Parent Marital Status', type: 'select',
        options: ['married', 'divorced', 'separated', 'widowed', 'single', 'unmarried'] }
    ]
  },
  {
    title: 'Household',
    fields: [
      { key: 'q_household_size', label: 'Household Size', type: 'number', placeholder: '1' },
      { key: 'q_college_count', label: 'Number in College', type: 'number', placeholder: '1' },
      { key: 'q_dep_count', label: 'Number of Dependents', type: 'number', placeholder: '0' }
    ]
  },
  {
    title: 'Taxes',
    fields: [
      { key: 'q_par_tax_form', label: 'Parent Tax Form Filed', type: 'select',
        options: ['1040', '1040A', '1040EZ', 'foreign', 'none'] },
      { key: 'q_par_agi', label: 'Parent Adjusted Gross Income ($)', type: 'number', placeholder: '0' }
    ]
  },
  {
    title: 'Income & assets',
    fields: [
      { key: 'q_par_income', label: 'Parent Income ($)', type: 'number', placeholder: '0' },
      { key: 'q_par_assets', label: 'Parent Assets ($)', type: 'number', placeholder: '0' },
      { key: 'q_student_income', label: 'Student Income ($)', type: 'number', placeholder: '0' },
      { key: 'q_student_assets', label: 'Student Assets ($)', type: 'number', placeholder: '0' }
    ]
  }
];

const ALL_FIELDS = GROUPS.flatMap((g) => g.fields);
const INITIAL_PROFILE = Object.fromEntries(ALL_FIELDS.map((f) => [f.key, '']));

/* Empty strings aren't meaningful to the evaluator. Drop them. */
function compactProfile(p) {
  const out = {};
  for (const [k, v] of Object.entries(p)) {
    if (v === '' || v === null || v === undefined) continue;
    const num = Number(v);
    out[k] = !isNaN(num) && /^-?\d+(\.\d+)?$/.test(String(v)) ? num : v;
  }
  return out;
}

function sectionClass(section) {
  if (section.hidden) return 'cb-section-out hidden-sec';
  if (section.undetermined || section.status === 'undetermined') return 'cb-section-out undet-sec';
  if (section.menu?.toLowerCase().includes('hhb') || section.branch === 'hhb') return 'cb-section-out hhb-branch';
  return 'cb-section-out show';
}

function LinearTab({ result }) {
  const [expanded, setExpanded] = useState({});
  if (!result) return <div className="cb-empty">Change a field to evaluate.</div>;
  const sections = result.sections ?? [];
  if (sections.length === 0) return <div className="cb-empty">No sections match this profile.</div>;

  const toggle = (id) => setExpanded((s) => ({ ...s, [id]: !s[id] }));

  return (
    <div>
      {sections.map((s) => {
        const id = s.id ?? s.sectionId ?? s.name;
        const qs = s.questions ?? [];
        return (
          <div key={id} className={sectionClass(s)}>
            <div className="cb-section-head" onClick={() => toggle(id)} role="button" tabIndex={0}>
              <div>
                <div className="cb-section-title">{s.label ?? id}</div>
                <div className="cb-section-meta">{qs.length} question{qs.length !== 1 ? 's' : ''} · {s.menu ?? 'Ungrouped'}</div>
              </div>
              <div className="cb-row">
                {s.undetermined && <span className="cb-badge undet">undetermined</span>}
                {(s.menu?.toLowerCase().includes('hhb') || s.branch === 'hhb') && <span className="cb-badge hhb">HHB</span>}
                <span style={{ color: 'var(--cb-gray-4)' }}>{expanded[id] ? '▲' : '▼'}</span>
              </div>
            </div>
            {expanded[id] && qs.length > 0 && (
              <div className="cb-section-body">
                <ul className="cb-q-list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {qs.map((q) => {
                    const qid = q.id ?? q.questionId ?? q;
                    return (
                      <li key={qid} className="cb-q-item">
                        <span style={{ fontFamily: '"Roboto Mono", monospace', color: 'var(--cb-gray-4)', fontSize: 11, minWidth: 120 }}>{qid}</span>
                        <span className={q.hidden ? 'hidden' : ''}>{q.label ?? qid}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function TreeTab({ result }) {
  if (!result) return <div className="cb-empty">Change a field to evaluate.</div>;
  const sections = result.sections ?? [];
  const byMenu = sections.reduce((acc, s) => {
    const menu = s.menu ?? 'Ungrouped';
    (acc[menu] ||= []).push(s);
    return acc;
  }, {});
  if (Object.keys(byMenu).length === 0) return <div className="cb-empty">No sections.</div>;

  return (
    <div>
      {Object.entries(byMenu).map(([menu, secs]) => (
        <div key={menu} style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 11, fontWeight: 700, color: 'var(--cb-gray-5)', textTransform: 'uppercase', letterSpacing: '0.8px', margin: '0 0 8px 0' }}>{menu}</h3>
          <div style={{ paddingLeft: 12 }}>
            {secs.map((section) => {
              const id = section.id ?? section.sectionId ?? section.name;
              return (
                <details key={id} style={{ borderLeft: '2px solid var(--cb-blue-2)', paddingLeft: 12, marginBottom: 4 }}>
                  <summary style={{ cursor: 'pointer', fontSize: 13, padding: '4px 0' }}>{section.label ?? id}</summary>
                  <ul style={{ paddingLeft: 16, margin: '4px 0 0 0', listStyle: 'none' }}>
                    {(section.questions ?? []).map((q) => {
                      const qid = q.id ?? q.questionId ?? q;
                      return <li key={qid} style={{ fontSize: 12, color: 'var(--cb-gray-5)', padding: '2px 0 2px 8px', borderLeft: '1px solid var(--cb-gray-2)' }}>{q.label ?? qid}</li>;
                    })}
                  </ul>
                </details>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function VarsTab({ profile, result }) {
  const vars = result?.vars ?? result?.variables ?? null;
  const rows = vars ? Object.entries(vars) : Object.entries(profile);
  if (rows.length === 0) return <div className="cb-empty">No variables yet.</div>;
  const pillFor = (v) => {
    if (v === true || v === 'true') return <span className="cb-badge success">TRUE</span>;
    if (v === false || v === 'false') return <span className="cb-badge error">FALSE</span>;
    if (v === null || v === undefined || v === '') return <span className="cb-badge undet">—</span>;
    return <span>{String(v)}</span>;
  };
  return (
    <table className="cb-var-table">
      <thead><tr><th>Name</th><th>Value</th></tr></thead>
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k}><td>{k}</td><td>{pillFor(v)}</td></tr>
        ))}
      </tbody>
    </table>
  );
}

function DiffTab({ baseline, current }) {
  if (!baseline) {
    return <div className="cb-empty">Snapshot a baseline in the left column, then change a field to see the diff.</div>;
  }
  if (!current) return <div className="cb-empty">No current evaluation yet.</div>;

  const baseIds = new Set((baseline.sections ?? []).map((s) => s.id ?? s.sectionId ?? s.name));
  const curIds = new Set((current.sections ?? []).map((s) => s.id ?? s.sectionId ?? s.name));
  const added = [...curIds].filter((x) => !baseIds.has(x));
  const removed = [...baseIds].filter((x) => !curIds.has(x));

  return (
    <div className="cb-diff-col">
      <div className="col">
        <h3>Added sections</h3>
        {added.length === 0 ? <p className="cb-muted">None</p> : (
          <ul style={{ margin: 0, paddingLeft: 16 }}>{added.map((id) => <li key={id} className="cb-added">{id}</li>)}</ul>
        )}
      </div>
      <div className="col">
        <h3>Removed sections</h3>
        {removed.length === 0 ? <p className="cb-muted">None</p> : (
          <ul style={{ margin: 0, paddingLeft: 16 }}>{removed.map((id) => <li key={id} className="cb-removed">{id}</li>)}</ul>
        )}
      </div>
    </div>
  );
}

export default function BuilderView({ onResult, setHeader }) {
  const [profile, setProfile] = useState(INITIAL_PROFILE);
  const [specs, setSpecs] = useState([]);
  const [specVersion, setSpecVersion] = useState('');
  const [personas, setPersonas] = useState([]);
  const [activePersonaId, setActivePersonaId] = useState(null);
  const [profileName, setProfileName] = useState('Untitled');
  const [result, setResult] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [tab, setTab] = useState('linear');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dirty, setDirty] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    api.listSpecs().then((data) => {
      const list = Array.isArray(data) ? data : data.items ?? data.specs ?? [];
      setSpecs(list);
      const active = list.find((s) => s.status === 'active') ?? list[0];
      if (active) setSpecVersion(active.label ?? active);
    }).catch(() => {});

    api.listPersonas().then((data) => {
      const list = Array.isArray(data) ? data : data.items ?? [];
      setPersonas(list);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (typeof setHeader === 'function') setHeader({ specLabel: specVersion, profileName });
  }, [specVersion, profileName, setHeader]);

  const evaluate = useCallback(async (sv, prof) => {
    if (!sv) return;
    const compact = compactProfile(prof);
    console.log('[BuilderView] evaluate', { sv, profile: compact });
    setLoading(true);
    setError(null);
    try {
      const r = await api.evaluate(sv, compact);
      console.log('[BuilderView] evaluate result', r);
      setResult(r);
      if (typeof onResult === 'function') onResult(r);
    } catch (e) {
      console.error('[BuilderView] evaluate error', e);
      setError(e.message || 'Evaluation failed');
    } finally {
      setLoading(false);
    }
  }, [onResult]);

  useEffect(() => {
    if (!specVersion) return;
    if (!dirty) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => evaluate(specVersion, profile), 300);
    return () => clearTimeout(debounceRef.current);
  }, [profile, specVersion, evaluate, dirty]);

  const handleField = (key, value) => {
    setDirty(true);
    setActivePersonaId(null);
    setProfile((p) => ({ ...p, [key]: value }));
  };

  const applyPersona = (p) => {
    setDirty(true);
    setActivePersonaId(p.id);
    setProfileName(p.name);
    setProfile({ ...INITIAL_PROFILE, ...(p.profile || {}) });
  };

  const snapshotBaseline = () => {
    if (result) setBaseline(result);
  };

  const stats = useMemo(() => {
    const sections = result?.sections ?? [];
    return {
      sections: sections.length,
      questions: sections.reduce((n, s) => n + (s.questions?.length ?? 0), 0),
      hhb: sections.filter((s) => s.menu?.toLowerCase().includes('hhb') || s.branch === 'hhb').length,
      undet: sections.filter((s) => s.undetermined).length,
      hidden: sections.filter((s) => s.hidden).length
    };
  }, [result]);

  return (
    <div className="cb-workspace">
      <div className="cb-left">
        <div className="cb-card">
          <h2>Spec Version</h2>
          <div className="cb-field">
            <select value={specVersion} onChange={(e) => { setSpecVersion(e.target.value); setDirty(true); }}>
              {specs.length === 0 && <option value="">No specs available</option>}
              {specs.map((s) => {
                const label = typeof s === 'string' ? s : s.label;
                const status = typeof s === 'string' ? '' : s.status;
                return <option key={label} value={label}>{label}{status ? ` (${status})` : ''}</option>;
              })}
            </select>
          </div>
        </div>

        <div className="cb-card">
          <h2>Personas</h2>
          {personas.length === 0 ? (
            <p className="cb-muted" style={{ fontSize: 12, margin: 0 }}>No personas saved. Create one on the Personas page.</p>
          ) : (
            <div>
              {personas.map((p) => (
                <div
                  key={p.id}
                  className={`cb-persona-row${activePersonaId === p.id ? ' active' : ''}`}
                  onClick={() => applyPersona(p)}
                  role="button"
                  tabIndex={0}
                >
                  {p.name}
                  <span className="cb-tag">{p.visibility}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {GROUPS.map((group) => (
          <div key={group.title} className="cb-card">
            <h2>{group.title}</h2>
            {group.fields.map(({ key, label, type, options, placeholder }) => (
              <div key={key} className="cb-field">
                <label htmlFor={key}>{label}</label>
                {type === 'select' ? (
                  <select id={key} value={profile[key]} onChange={(e) => handleField(key, e.target.value)}>
                    <option value="">— select —</option>
                    {options.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input id={key} type="number" value={profile[key]} placeholder={placeholder}
                    onChange={(e) => handleField(key, e.target.value)} />
                )}
              </div>
            ))}
          </div>
        ))}

        <div className="cb-card">
          <h2>Diff baseline</h2>
          <button className="cb-btn sm" onClick={snapshotBaseline} disabled={!result}>
            Snapshot current as baseline
          </button>
          <div style={{ fontSize: 11, color: 'var(--cb-gray-4)', marginTop: 8 }}>
            {baseline ? 'Baseline saved. Change a field, then view Diff tab.' : 'No baseline saved yet.'}
          </div>
        </div>
      </div>

      <div className="cb-right">
        <div className="cb-tabs" role="tablist">
          {['linear', 'tree', 'vars', 'diff'].map((t) => (
            <button key={t} role="tab" aria-selected={tab === t}
              className={`cb-tab${tab === t ? ' active' : ''}`}
              onClick={() => setTab(t)}
            >
              {t === 'linear' ? 'Linear flow' : t[0].toUpperCase() + t.slice(1)}
            </button>
          ))}
          <div style={{ marginLeft: 'auto', alignSelf: 'center', paddingRight: 20, fontSize: 12, color: 'var(--cb-gray-4)', display: 'flex', alignItems: 'center', gap: 12 }}>
            {loading && 'Evaluating…'}
            <button className="cb-btn sm primary" onClick={() => evaluate(specVersion, profile)} disabled={!specVersion || loading}>
              Evaluate
            </button>
          </div>
        </div>

        <div className="cb-tab-body">
          {error && <div className="cb-alert" role="alert">{error}</div>}
          {tab === 'linear' && <LinearTab result={result} />}
          {tab === 'tree' && <TreeTab result={result} />}
          {tab === 'vars' && <VarsTab profile={profile} result={result} />}
          {tab === 'diff' && <DiffTab baseline={baseline} current={result} />}
        </div>

        <div className="cb-stats">
          <div className="cb-stat"><span className="cb-dot hha" /><strong>{stats.sections}</strong> sections shown</div>
          <div className="cb-stat"><span className="cb-dot hha" /><strong>{stats.questions}</strong> questions</div>
          <div className="cb-stat"><span className="cb-dot hhb" /><strong>{stats.hhb}</strong> HHB sections</div>
          <div className="cb-stat"><span className="cb-dot undet" /><strong>{stats.undet}</strong> undetermined</div>
          <div className="cb-stat"><span className="cb-dot warn" /><strong>{stats.hidden}</strong> hidden</div>
        </div>
      </div>
    </div>
  );
}
